import { Injectable } from '@angular/core';
import Endpoints from '../endpoints';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LiveViewDTO, OwnOrganisationsAndSites } from '../types/common';
import { TranslateService } from '@ngx-translate/core';
import { colors, dashboardChartConfig } from '../../app.config';

@Injectable()
export class CommonService {
  constructor(private http: HttpClient,
              private translate: TranslateService) {}

  public getOwnOrganisationsAndSites(): Observable<OwnOrganisationsAndSites[]> {
    return this.http.get<OwnOrganisationsAndSites[]>(Endpoints.GET_OWN_ORGANISATIONS_AND_SITES);
  }

  public getLiveView(params: any): Observable<LiveViewDTO[]> {
    return this.http.get<LiveViewDTO[]>(Endpoints.GET_LIVE_VIEW, { params: params });
  }


  public getAgeAndGenderDistributionChartConfig(dataset: any) {
    const datasets = [];
    let ageGroups = dataset.map(item => item.label);
    const maleValues = dataset.map(item => -item.male.count); // (-) előjel, balra nyújtás
    const femaleValues = dataset.map(item => item.female.count);
    const absoluteMax = Math.max(Math.max(...femaleValues), Math.abs(Math.min(...maleValues)));

    const rawDatasets = [
      { label: this.translate.instant("DASHBOARD.MALE"), data: maleValues, color: colors.primary },
      { label: this.translate.instant("DASHBOARD.FEMALE"), data: femaleValues, color: colors.secondary }
    ];
    for (const item of rawDatasets) {
      datasets.push({
        label: item.label,
        data: item.data,
        backgroundColor: item.color,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
        stacked: true
      });
    }
    const data = {
      labels: ageGroups,
      datasets: datasets
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: true },
        tooltip: {
          callbacks: {
            // A tooltipben (lebegő ablakban) eltüntetjük a mínusz jelet a férfiaknál
            label: (context: any) => {
              const value = Math.abs(context.raw);
              return `${context.dataset.label}: ${value}`;
            }
          }
        }
      },
      scales: {
        x: {
          stacked: false,
          min: -absoluteMax,
          max: absoluteMax,
          ticks: {
            stepSize: 2,
            callback: (value: any) => { return Math.abs(value) }
          }
        },
        y: {
          stacked: true,
          grid: { display: false }
        }
      }
    };

    return { type: 'bar', data: data, options: options };
  }

  public getNewVsReturningVisitorsChartConfig(dataset: any) {
    const newCount = dataset.new.count;
    const returningCount = dataset.returning.count;
    const total = newCount + returningCount;
    const newVisitorsPercentage = total > 0 ? Math.round((newCount / total) * 100) : 0;

    const data = {
      newVisitorsPercentage,
      returningVisitorsPercentage: total > 0 ? 100 - newVisitorsPercentage : 0
    }
    const legendData = {
      newVisitors: newCount,
      returningVisitors: returningCount
    }

    return { data: data, legendData: legendData }
  }

  public getGenderDistributionChartConfig(dataset: any) {
    const male = dataset.categories.find(item => item.gender === 'male');
    const female = dataset.categories.find(item => item.gender === 'female');
    const unknown = dataset.excludedUnknownCount;
    const totalVisitors = male.count + female.count; // dataset.displayedCount
    const legendData = [
      { label: this.translate.instant("DASHBOARD.MALE"), value: male.count, percentage: male.percentage, color: colors.primary },
      { label: this.translate.instant("DASHBOARD.FEMALE"), value: female.count, percentage: female.percentage, color: colors.secondary },
      /*{ label: this.translate.instant("DASHBOARD.UNKNOWN_UNCERTAIN"), value: unknown, percentage: Math.round((unknown / totalVisitors) * 100), color: colors.grey }*/
    ];

    const data = {
      labels: legendData.map(item => item.label),
      datasets: [
        {
          data: legendData.map(item => item.value),
          backgroundColor: legendData.map(item => item.color),
          hoverBackgroundColor: legendData.map(item => item.color),
          borderWidth: 2,
          borderColor: '#ffffff'
        }
      ]
    };

    const options = {
      ...dashboardChartConfig,
      cutout: '65%',
      plugins: {
        ...dashboardChartConfig.plugins,
        legend: { display: false },
      },
      scales: {
        x: { display: false }
      }
    };

    return { type: 'doughnut', data: data, options: options, legendData: legendData, totalVisitors: totalVisitors };
  }
}
